import i18n, { BackendModule, FallbackLng } from "i18next";
import { initReactI18next } from "react-i18next";
import { Mutex } from "async-mutex";
import Backend, { ChainedBackendOptions } from "i18next-chained-backend";
import { HttpBackendOptions } from "i18next-http-backend";
import LocalStorageBackend, {
  LocalStorageBackendOptions,
} from "i18next-localstorage-backend";
import HttpApi from "i18next-http-backend"; // fallback http load
import { createMissingNamespaceGuardBackend } from "./MissingNamespaceGuardBackend";
const mutex = new Mutex();
let initialized = false;

export class I18NextInitializer {

  readonly defaultVersion: string | null;
  readonly lng?: string;
  readonly fallbackLng?: false | FallbackLng;
  readonly supportedLngs?: Array<string>;
  readonly debug?: boolean;
  readonly prependBackends?: Array<BackendModule>;
  readonly errorOnMissingNamespace: boolean;

  constructor(args: {
    // Pass a stable string (e.g. the app's build version) to enable the
    // LocalStorageBackend cache keyed by that version.
    // Pass `null` to opt out of LocalStorageBackend entirely (only HttpApi
    // is used) — translations are fetched on every page load.
    // The literal "local-development" enables the backend but bumps the
    // version on every init so local edits are always picked up — this is
    // intentional so we exercise the LocalStorageBackend code path in dev.
    defaultVersion: string | null;
    lng?: string;
    fallbackLng?: false | FallbackLng;
    // The set of on-disk locale-file basenames the app supports (e.g.
    // ["en", "no"]). Forwarded to i18next together with
    // `nonExplicitSupportedLngs: true` so that locale variants such as
    // `nb-NO` collapse to `no` and `en-US` to `en` before any HTTP
    // request is issued. See PRD i18n-locale-file-sync.
    supportedLngs?: Array<string>;
    debug?: boolean;
    // Backends contributed by consuming libraries/apps. They are placed at
    // the FRONT of the chained-backend chain, ahead of the default
    // `[LocalStorageBackend?, HttpApi]` chain, so a contributed backend can
    // serve its own namespaces first. Matrikkel-agnostic: any library can
    // contribute a backend. Omitting this reproduces the default chain
    // exactly. See prd-matrikkel-kodeliste-i18n-backend-plugin (ADR 0003).
    prependBackends?: Array<BackendModule>;
    // When `true` (the default), a guard backend is prepended to the front of
    // the chain that intercepts the fallback `translation` namespace that
    // react-i18next uses when `useTranslation()` is called WITHOUT an explicit
    // namespace. It logs a loud error and returns an empty bundle so no 404
    // request is issued for `assets/i18n/translation/{{lng}}.json`. Set to
    // `false` only if an app legitimately serves a `translation` namespace.
    // See MissingNamespaceGuardBackend.
    errorOnMissingNamespace?: boolean;
  }) {
    this.defaultVersion = args.defaultVersion;
    this.lng = args.lng;
    this.fallbackLng = args.fallbackLng;
    this.supportedLngs = args.supportedLngs;
    this.debug = args.debug;
    this.prependBackends = args.prependBackends;
    this.errorOnMissingNamespace = args.errorOnMissingNamespace ?? true;
  }

  async init(): Promise<typeof i18n> {
    return mutex.runExclusive(async () => {
      if (initialized) {
        return i18n;
      }

      const httpBackendOptions: HttpBackendOptions = {
        loadPath: "assets/i18n/{{ns}}/{{lng}}.json",
        reloadInterval: false,
      };

      const backends: Array<unknown> = [];
      const backendOptions: Array<unknown> = [];

      // The missing-namespace guard goes at the very front of the chain so it
      // can intercept the fallback `translation` namespace (used by no-arg
      // useTranslation() calls) before any other backend issues an HTTP
      // request for it. See MissingNamespaceGuardBackend.
      if (this.errorOnMissingNamespace) {
        backends.push(createMissingNamespaceGuardBackend());
        backendOptions.push({});
      }

      // Prepended backends go at the front of the chain. Each contributed
      // backend manages its own options internally, so an empty options entry
      // keeps the `backends`/`backendOptions` arrays index-aligned.
      for (const prependBackend of this.prependBackends ?? []) {
        backends.push(prependBackend);
        backendOptions.push({});
      }

      if (this.defaultVersion !== null) {
        const effectiveVersion =
          this.defaultVersion === "local-development"
            ? `local-development-${Date.now()}`
            : this.defaultVersion;
        const localStorageBackendOptions: LocalStorageBackendOptions = {
          defaultVersion: effectiveVersion,
          // Effectively infinite: the cache is already keyed on the build
          // version (`defaultVersion`), so a new build changes the key and
          // invalidates the cache. The default 7-day time expiry is therefore
          // redundant and offline-hostile ("cache forever until the build
          // number changes"). See
          // prd-matrikkel-kodeliste-i18n-backend-plugin (ADR 0003).
          expirationTime: Number.MAX_SAFE_INTEGER,
        };
        backends.push(LocalStorageBackend);
        backendOptions.push(localStorageBackendOptions);
      }
      backends.push(HttpApi);
      backendOptions.push(httpBackendOptions);

      const instance = i18n.use(Backend).use(initReactI18next);
      await instance.init<ChainedBackendOptions>(
        {
          lng: this.lng,
          fallbackLng: this.fallbackLng,
          supportedLngs: this.supportedLngs,
          nonExplicitSupportedLngs: true,
          load: "currentOnly",
          debug: this.debug,
          nsSeparator: false,
          ns: [], // don't preload any namespace on init
          defaultNS: false, // don't fall back to a 'translation' namespace
          partialBundledLanguages: true,
          interpolation: {
            escapeValue: false,
          },
          backend: {
            backends,
            backendOptions,
          },
        },
        () => {
          initialized = true;
        },
      );
      return instance;
    });
  }
}
